import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import List from '@mui/material/List';
import MuiListItem, {
  ListItemProps as MuiListItemProps,
} from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton, {
  ListItemButtonProps as MuiListItemButtonProps,
} from '@mui/material/ListItemButton';
import { default as MuiListItemIcon } from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';

import { AvatarSkeleton } from './Avatar';
import { Box } from './Box';
import { Icon, IconProps } from './Icon';

export { List };

type IndentAmount = 'auto' | 'medium' | 'large';

type BaseListItemProps = {
  onClick?: (e?: React.MouseEvent) => void;
  leftAdornment?: React.ReactNode;
  rightAdornment?: React.ReactNode;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  avatar?: React.ReactNode;
  disablePadding?: boolean;
  clickable?: boolean;
  rounded?: boolean;
  icon?: IconDefinition;
  indent?: IndentAmount;
  centerIndent?: boolean;
  iconColor?: IconProps['color'];
  primaryTypographyProps?: React.HTMLAttributes<HTMLSpanElement>;
  disabled?: boolean;
};

type ConditionalSelected<T> = T extends { clickable?: false }
  ? { selected?: never }
  : T extends { clickable?: any } | { onClick: () => void }
  ? { selected?: boolean }
  : { selected?: never };

export type ListItemProps = BaseListItemProps &
  ConditionalSelected<BaseListItemProps>;

export function ListItem<C extends React.ElementType = 'button'>(
  props: MuiListItemProps<C, { component?: C } & ListItemProps>
) {
  const {
    onClick,
    leftAdornment,
    rightAdornment,
    primary,
    secondary,
    avatar,
    icon,
    disablePadding,
    iconColor,
    component,
    clickable,
    rounded,
    primaryTypographyProps,
    centerIndent,
    indent = 'auto',
    sx,
    selected,
    disabled,
    ...otherProps
  } = props;

  const useButton = clickable || (onClick && clickable !== false);

  const hasIndentElement = leftAdornment || icon || avatar;
  const needsEmptyIndent = !hasIndentElement && indent !== 'auto';

  const listItemStyles = [
    !!rounded && {
      borderRadius: 1,
    },
    ...(Array.isArray(sx) ? sx : [sx]),
  ];

  const indentStyles = [
    !!getMinWidth(indent) && {
      m: 0,
      minWidth: getMinWidth(indent),
    },
    !!centerIndent && {
      justifyContent: 'center',
    },
  ];

  const listItemContent = (
    <>
      {leftAdornment && (
        <Box sx={[{ display: 'flex' }, ...indentStyles]}>{leftAdornment}</Box>
      )}
      {icon && (
        <MuiListItemIcon sx={indentStyles}>
          <Icon color={iconColor} icon={icon} />
        </MuiListItemIcon>
      )}
      {avatar && <ListItemAvatar sx={indentStyles}>{avatar}</ListItemAvatar>}
      {needsEmptyIndent && <Box sx={indentStyles} />}
      <ListItemText
        color="text"
        primary={primary}
        secondary={secondary}
        primaryTypographyProps={primaryTypographyProps}
      />
      {rightAdornment}
    </>
  );

  if (useButton) {
    return (
      <ListItemButton
        onClick={onClick}
        component={component}
        sx={listItemStyles}
        selected={selected}
        disabled={disabled}
        {...(otherProps as MuiListItemButtonProps)}
      >
        {listItemContent}
      </ListItemButton>
    );
  }

  return (
    <MuiListItem
      disablePadding={disablePadding}
      onClick={onClick}
      component={component}
      sx={listItemStyles}
      disabled={disabled}
      {...otherProps}
    >
      {listItemContent}
    </MuiListItem>
  );
}

function getMinWidth(amount: IndentAmount) {
  if (amount === 'auto') {
    return undefined;
  }
  if (amount === 'medium') {
    return '20px';
  }
  if (amount === 'large') {
    return '40px';
  }
}

export interface ListItemSkeletonProps {
  primary?: boolean;
  primaryWidth?: string;
  secondary?: boolean;
  secondaryWidth?: string;
  avatar?: boolean;
  sx?: MuiListItemProps['sx'];
}

export const ListItemSkeleton: React.FC<ListItemSkeletonProps> = ({
  primary,
  primaryWidth = '70%',
  secondary,
  secondaryWidth = '50%',
  avatar,
  sx,
}) => {
  return (
    <MuiListItem sx={sx}>
      {avatar && (
        <ListItemAvatar>
          <AvatarSkeleton />
        </ListItemAvatar>
      )}
      <ListItemText
        primary={
          primary && <Skeleton variant="text" width={primaryWidth}></Skeleton>
        }
        secondary={
          secondary && (
            <Skeleton variant="text" width={secondaryWidth}></Skeleton>
          )
        }
      />
    </MuiListItem>
  );
};
